import { Value } from './../../interfaces/indicator-detail';
class IndexedDB {
  private static instance: IndexedDB | null = null;
  private db: IDBDatabase | null = null;
  private readonly storeName: string =
    process.env.NEXT_PUBLIC_INDEXED_DB_DEFAULT_STORE || '';

  private constructor() {}

  public static async getInstance(): Promise<IndexedDB> {
    if (!IndexedDB.instance) {
      IndexedDB.instance = new IndexedDB();
    }
    const dbName = process.env.NEXT_PUBLIC_INDEXED_DB_NAME || 'default_db';
    const dbVersion = parseInt(
      process.env.NEXT_PUBLIC_INDEXED_DB_VERSION || '1',
      10,
    );

    await IndexedDB.instance.connect(dbName, dbVersion);
    return IndexedDB.instance;
  }

  private async connect(dbName: string, version: number): Promise<void> {
    if (this.db) {
      this.close();
    }

    this.db = await new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, version);

      request.onupgradeneeded = (event) => {
        console.log('✅ Upgrade needed for IndexedDB');
        const db = request.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, {
            keyPath: 'id',
            autoIncrement: true,
          });
        }
      };

      request.onsuccess = () => {
        console.log('✅ IndexedDB connected successfully');
        resolve(request.result);
      };

      request.onerror = (event) => {
        console.error('❌ Failed to connect to IndexedDB:', request.error);
        reject(request.error);
      };
    });
  }

  private ensureConnected(): void {
    if (!this.db) {
      throw new Error('❌ Database is not connected');
    }
  }

  public async get(key: string | number): Promise<any> {
    this.ensureConnected();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(this.storeName, 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(key);

      request.onsuccess = () => {
        console.log('✅ IndexedDB get successfully');
        resolve(request.result?.value);
      };
      request.onerror = () => {
        console.error('❌ Error fetching data:', request.error);
        reject(request.error);
      };
    });
  }

  public async set(value: any, key?: string | number): Promise<void> {
    this.ensureConnected();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);

      const request = key ? store.put({ ...value, id: key }) : store.add(value);

      request.onsuccess = () => {
        console.log('✅ IndexedDB set successfully');
        resolve();
      };
      request.onerror = () => {
        console.error('❌ Error setting data:', request.error);
        reject(request.error);
      };
    });
  }

  public async delete(key: string | number): Promise<void> {
    this.ensureConnected();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(key);

      request.onsuccess = () => {
        console.log('✅ IndexedDB delete successfully');
        resolve();
      };
      request.onerror = () => {
        console.error('❌ Error deleting data:', request.error);
        reject(request.error);
      };
    });
  }

  public async clearAll(): Promise<void> {
    this.ensureConnected();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();

      request.onsuccess = () => {
        console.log('✅ IndexedDB clearAll successfully');
        resolve();
      };
      request.onerror = () => {
        console.error('❌ Error clearing data:', request.error);
        reject(request.error);
      };
    });
  }

  public close(): void {
    if (this.db) {
      console.log('🚀 Closing IndexedDB connection...');
      this.db.close();
      this.db = null;
    }
  }
}

export default IndexedDB;
